<template lang="pug">
  CommonSection.b-spec-import-page(
    title="Проверка импорта"
    data-testid="import-check-page"
    :back="{ title: 'Спецификация', name: 'project-specification', params: { projectId } }"
    )
    ._layout(v-if="job")
      ._preview(data-testid="import-check-preview")
        ._file(v-for="file in job.files" :key="file.id")
          ._file-name {{ file.name }}
          VuePdfEmbed(v-if="isPdf(file)" :source="file.url")
          img._image(v-else-if="isImage(file)" :src="file.url" :alt="file.name")
          pre._text(v-else) {{ preparedText(file) }}

      ._rows
        ._not-found(v-if="!rows.length" data-testid="import-check-not-found") Позиции не найдены
        table._table(v-else)
          thead
            tr
              th
              th Фото
              th Наименование
              th Категория
              th Помещение
              th Кол-во план
              th Ед.
              th Бренд
              th Серия
              th Артикул
              th Цвет / материал
              th Габариты
              th Цена
              th Уверенность
              th
          tbody
            tr._row(v-for="(row, index) in rows" :key="index" data-testid="import-check-row")
              td
                input._check(type="checkbox" data-testid="import-check-row-checkbox" v-model="row.checked")
              td
                ._photo(v-if="row.photo" data-testid="import-check-row-photo")
                  img._photo-img(:src="photoUrl(row)" :alt="row.name")
                  button._photo-remove(type="button" title="Не ставить фото" data-testid="import-check-row-photo-remove" @click="row.photo = null") ×
              td
                SpecCell(
                  data-testid="import-check-row-name"
                  :value="row.name"
                  :display="row.name"
                  :active="editing === index"
                  @edit="editing = index"
                  @close="editing = null"
                  @save="row.name = $event"
                  )
                span._duplicate(v-if="row.duplicateOf" data-testid="import-check-row-duplicate") дубль существующей позиции
                select._select(v-if="row.duplicateOf" data-testid="import-check-row-decision" v-model="row.decision")
                  option(value="update" data-testid="import-check-row-decision-option") Обновить существующую
                  option(value="create" data-testid="import-check-row-decision-option") Создать новую
                  option(value="skip" data-testid="import-check-row-decision-option") Пропустить
              td
                select._select(data-testid="import-check-row-category" v-model="row.category")
                  option(value="") — категория —
                  option(v-for="(title, code) in CATEGORIES" :key="code" :value="code") {{ title }}
              td
                select._select(data-testid="import-check-row-room" v-model="row.room")
                  option(value="") — не задано —
                  option(v-for="room in roomOptions(row)" :key="room" :value="room") {{ room }}
                span._mismatch(v-if="isRoomMismatch(row)" data-testid="import-check-row-room-mismatch") нет такого помещения
              td
                input._input.narrow(v-model="row.quantity")
              td
                select._select(v-model="row.unit")
                  option(v-for="(title, code) in UNITS" :key="code" :value="code") {{ title }}
              td
                input._input(data-testid="import-check-row-brand" v-model="row.brand")
              td
                input._input(data-testid="import-check-row-series" v-model="row.series")
              td
                input._input(data-testid="import-check-row-article" v-model="row.article")
              td
                input._input(data-testid="import-check-row-color" v-model="row.colorMaterial")
              td
                input._input(data-testid="import-check-row-dimensions" v-model="row.dimensions")
              td
                input._input.narrow(data-testid="import-check-row-price" v-model="row.price")
              td
                span._confidence.high(v-if="row.confidence === 'high'" data-testid="import-check-row-confidence-high" title="высокая уверенность") ●
                span._confidence.medium(v-else-if="row.confidence === 'medium'" data-testid="import-check-row-confidence-medium" title="средняя уверенность") ●
                span._confidence.low(v-else data-testid="import-check-row-confidence-low" title="низкая уверенность") ●
              td
                button._delete(type="button" data-testid="import-check-row-delete" @click="remove(index)") ×

        ._buttons
          Btn(blue data-testid="import-check-submit" :disabled="busy" @click="submit") Создать позиции ({{ activeCount }})
          Btn(grey data-testid="import-check-cancel" @click="back") Отмена
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import CommonSection from '@/components/CommonSection.vue';
import SpecCell from '@/components/specification/SpecCell.vue';
import specAiJobApi from '@/api/specAiJob';
import model from '@/model/specification';
import { apiRun } from '@/model/apiRun';
import { CATEGORIES, UNITS, projectRooms } from '@/utils/spec';

/**
 * Экран проверки импорта (ТЗ 6.2, 5.2): слева просмотр документа (pdf и изображения —
 * встроенным просмотрщиком, таблицы и docx — подготовленным текстом из результата задания),
 * справа черновик со всеми полями, которые даёт импорт (5.2): наименование, категория,
 * помещение, количество, единица, бренд, серия, артикул, цвет/материал, габариты, цена.
 *
 * Помещение со списком помещений проекта сопоставляет сервер (ТЗ 5.2) — в строке уже стоит
 * написание проекта; здесь только подсвечивается несовпавшее. Решение по дублю по умолчанию —
 * «Обновить существующую».
 *
 * Фото строки — картинка из этой строки файла xlsx или docx, её достаёт сервер. Дизайнер может снять
 * фото со строки; у существующей позиции сервер ставит его, только если своего фото нет.
 * Сгенерировано Claude.
 */
export default {
  name: 'SpecificationImportPage',
  components: { CommonSection, SpecCell, VuePdfEmbed },
  props: {
    project: Object,
  },
  data() {
    return {
      CATEGORIES,
      UNITS,
      job: null,
      rows: [],
      editing: null,
      busy: false,
    };
  },
  computed: {
    projectId() { return Number(this.$route.params.projectId); },
    jobId() { return Number(this.$route.params.jobId); },
    rooms() { return projectRooms(this.project); },
    activeCount() { return this.rows.filter((row) => row.checked && row.decision !== 'skip').length; },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.job = await specAiJobApi.fetchItem(this.jobId, {}, true);
      this.rows = (this.job.result?.rows ?? []).map((row) => ({
        ...row,
        checked: true,
        decision: row.duplicateOf ? 'update' : 'create',
        room: row.room ?? '',
        category: row.category ?? '',
        unit: row.unit ?? 'pcs',
        photo: row.photo ?? null,
      }));
    },
    photoUrl(row) { return row.photo?.thumbnails?.avatar ?? row.photo?.url ?? null; },
    isPdf(file) { return (file.extension ?? '').toLowerCase() === 'pdf' || file.type === 'application/pdf'; },
    isImage(file) { return String(file.type ?? '').startsWith('image/'); },
    preparedText(file) {
      const source = (this.job?.result?.sources ?? []).find((s) => Number(s.fileId) === file.id);
      return source?.preparedText ?? 'Текст документа недоступен';
    },
    roomOptions(row) {
      const rooms = [...this.rooms];
      if (row.room && !rooms.includes(row.room)) rooms.push(row.room);
      return rooms;
    },
    isRoomMismatch(row) {
      return !!row.room && !this.rooms.includes(row.room);
    },
    remove(index) {
      this.rows = this.rows.filter((row, i) => i !== index);
    },
    back() {
      this.$router.push({ name: 'project-specification', params: { projectId: this.projectId } });
    },
    async submit() {
      if (this.busy) return;
      this.busy = true;
      const payload = this.rows.map((row) => ({
        decision: row.checked ? row.decision : 'skip',
        itemId: row.duplicateOf ?? null,
        name: row.name,
        brand: row.brand,
        series: row.series,
        article: row.article,
        colorMaterial: row.colorMaterial,
        dimensions: row.dimensions,
        room: row.room || null,
        quantity: row.quantity,
        unit: row.unit,
        category: row.category,
        price: row.price,
        photoFileId: row.photo?.id ?? null,
      }));

      const applied = await apiRun(
        () => specAiJobApi.applyImport(this.jobId, payload, true),
        'Не удалось применить результат',
      );
      if (!applied) {
        this.busy = false;
        return;
      }
      await model.refresh(this.projectId);
      this.back();
    },
  },
};
</script>

<style scoped lang="scss">
.spec-import-page {
  &__layout {
    display: flex;
    gap: 20px;
    align-items: flex-start;
  }

  &__preview {
    width: 420px;
    flex: 0 0 420px;
    max-height: 75vh;
    overflow: auto;
    padding: 12px;
    box-sizing: border-box;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
  }

  &__file-name {
    display: block;
    font-size: 12px;
    color: var(--color-middle-gray);
    margin-bottom: 6px;
  }

  &__image {
    max-width: 100%;
  }

  &__text {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 11px;
    font-family: inherit;
    margin: 0;
  }

  &__rows {
    flex: 1 1 0;
    min-width: 0;
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th,
    td {
      text-align: left;
      padding: 4px 6px;
      border-bottom: 1px solid var(--color-super-light);
      vertical-align: top;
    }

    th {
      color: var(--color-middle-gray);
      font-weight: 400;
      white-space: nowrap;
    }
  }

  &__check {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  &__photo {
    position: relative;
    width: 40px;
    height: 40px;
  }

  &__photo-img {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
  }

  &__photo-remove {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--color-white);
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
    font-size: 12px;
    line-height: 16px;
    cursor: pointer;
  }

  &__input,
  &__select {
    box-sizing: border-box;
    width: 100%;
    min-width: 90px;
    padding: 3px 5px;
    border: 1px solid var(--color-super-light);
    border-radius: 4px;
    font: inherit;
    font-size: 12px;

    &.narrow {
      min-width: 60px;
    }
  }

  &__duplicate {
    display: block;
    font-size: 11px;
    color: var(--color-orange);
  }

  &__mismatch {
    display: block;
    font-size: 11px;
    color: var(--color-red);
  }

  &__confidence {
    font-size: 16px;
    line-height: 1;

    // уверенность распознавания цветом (ТЗ 6.2)
    &.high { color: var(--color-green); }
    &.medium { color: var(--color-yellow); }
    &.low { color: var(--color-red); }
  }

  &__delete {
    border: none;
    background: none;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
  }

  &__buttons {
    display: flex;
    gap: 12px;
    margin-top: 16px;
  }
}
</style>
